import { apiClient } from '@/lib/api/client';
import type {
    CheckLikesRequest,
    LikedMapResponse,
    ToggleLikeRequest,
    ToggleLikeResponse,
} from '@/features/likes/types/likeTypes';
import type { Business, Event, Artist } from '@/features/clubs/types/types';

/**
 * Service for like-related API calls.
 * All endpoints require authenticated user (cookies).
 */
export const likeService = {
    /**
     * Check which entities from a list are liked by the current user.
     * POST /api/public/v1/like/check
     * Response: { data: { liked_map: {...} } }
     */
    async checkLikes(request: CheckLikesRequest): Promise<LikedMapResponse> {
        try {
            console.log('checkLikes request body:', JSON.stringify(request));
            const response = await apiClient.post<{ data: LikedMapResponse }>(
                '/api/public/v1/like/check',
                request
            );
            // API returns { data: { liked_map: {...} } }
            return response.data || { liked_map: {} };
        } catch (error) {
            console.error('Error checking likes:', error);
            // Return empty map on error to avoid breaking UI
            return { liked_map: {} };
        }
    },

    /**
     * Toggle the like status for a single entity.
     * POST /api/public/v1/like/toggle
     * Response: { data: { liked: boolean } }
     */
    async toggleLike(request: ToggleLikeRequest): Promise<ToggleLikeResponse> {
        try {
            const response = await apiClient.post<{ data: ToggleLikeResponse }>(
                '/api/public/v1/like/toggle',
                request
            );
            // API returns { data: { liked: boolean } }
            return response.data;
        } catch (error) {
            console.error('Error toggling like:', error);
            throw new Error('Failed to toggle like');
        }
    },

    /**
     * Fetch all liked entities of a specific type.
     * GET /api/public/v1/like/all?entity_type=...
     * TODO: Add pagination (limit, offset query params)
     * Response format: { data: [{ business: {...} }] } for businesses, etc.
     */
    async fetchLikedBusinesses(): Promise<Business[]> {
        try {
            const response = await apiClient.get<{ data: { business: Business }[] }>(
                '/api/public/v1/like/all?entity_type=businesses'
            );
            // Extract business from each wrapper object
            return Array.isArray(response.data)
                ? response.data.map(item => item.business).filter(Boolean)
                : [];
        } catch (error) {
            console.error('Error fetching liked businesses:', error);
            return [];
        }
    },

    async fetchLikedEvents(): Promise<Event[]> {
        try {
            const response = await apiClient.get<{ data: { event: Event }[] }>(
                '/api/public/v1/like/all?entity_type=events'
            );
            // Extract event from each wrapper object
            return Array.isArray(response.data)
                ? response.data.map(item => item.event).filter(Boolean)
                : [];
        } catch (error) {
            console.error('Error fetching liked events:', error);
            return [];
        }
    },

    async fetchLikedArtists(): Promise<Artist[]> {
        try {
            const response = await apiClient.get<{ data: { artist: Artist }[] }>(
                '/api/public/v1/like/all?entity_type=artists'
            );
            // Extract artist from each wrapper object
            return Array.isArray(response.data)
                ? response.data.map(item => item.artist).filter(Boolean)
                : [];
        } catch (error) {
            console.error('Error fetching liked artists:', error);
            return [];
        }
    },

    /**
     * Claim all non-conflicting likes from guest user to real user.
     * Called during login/signup to transfer guest likes.
     * PUT /api/public/v1/like/claim
     */
    async claimLikes(): Promise<void> {
        try {
            await apiClient.put('/api/public/v1/like/claim');
        } catch (error) {
            console.error('Error claiming likes:', error);
            // Don't throw - claim failure shouldn't block login/signup
        }
    },
};
